import { useState, useEffect, useCallback } from 'react';
import { Spin, notification, Input, Modal } from 'antd';
import {
  BankOutlined, CalendarOutlined, CompassOutlined, EnvironmentOutlined, HeartOutlined,
  ReadOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../../config';
import { M, G, BG, ML } from '../shared/constants';
import { authHeader, useLang, tl, countryFlag, ssGet, ssSet } from '../shared/utils';
import { useDebounce } from '../shared/hooks';

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

export const PLATFORM_META = {
  whatsapp: { label: 'WhatsApp', color: '#25d366', bg: '#dcfce7' },
  facebook: { label: 'Facebook', color: '#1877f2', bg: '#dbeafe' },
  telegram: { label: 'Telegram', color: '#0088cc', bg: '#e0f2fe' },
};

const toTitle = s => s ? s.replace(/\b\w/g, c => c.toUpperCase()) : s;

export function fmtEventDate(dateStr, timeStr, userTz) {
  if (!dateStr) return '—';
  try {
    const base = timeStr ? `${dateStr}T${timeStr}` : `${dateStr}T00:00:00`;
    const ist  = new Date(base + '+05:30'); // events are stored in IST
    const opts = { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' };
    const dateOut = ist.toLocaleDateString('en-IN', opts);
    if (!timeStr) return dateOut;
    const timeOpts = { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' };
    const istTime  = ist.toLocaleTimeString('en-IN', { ...timeOpts, timeZone: 'Asia/Kolkata' });
    if (!userTz || userTz === 'Asia/Kolkata') return `${dateOut} · ${istTime}`;
    const localTime = ist.toLocaleTimeString('en-IN', { ...timeOpts, timeZone: userTz });
    return `${dateOut} · ${istTime} (${localTime})`;
  } catch { return dateStr; }
}

export function EventCard({ ev, lang, userTz }) {
  const t    = (hi, en) => lang === 'en' ? en : hi;
  const pm   = PLATFORM_META[ev.virtual_platform?.toLowerCase()] || {};
  const isVirtual  = ev.event_type === 'virtual';
  const isHybrid   = ev.event_type === 'hybrid';
  const isPhysical = ev.event_type === 'physical';
  const daysAway   = Number(ev.days_away);
  const isPast     = daysAway < -1;
  const isToday    = daysAway === 0;
  const isTomorrow = daysAway === 1;
  const isSoon     = daysAway > 0 && daysAway <= 7;

  const typeColor  = isVirtual ? '#1d4ed8' : isHybrid ? '#7c3aed' : M;
  const typeBg     = isVirtual ? '#eff6ff' : isHybrid ? '#f5f3ff' : '#fff5f5';
  const typeLabel  = isVirtual ? t('ऑनलाइन', 'Online') : isHybrid ? t('हाइब्रिड', 'Hybrid') : t('स्थानीय', 'In-Person');
  const typeIcon   = isVirtual ? '🌐' : isHybrid ? '🔀' : '📍';

  const title = (lang === 'en' && ev.title_en) ? ev.title_en : ev.title;
  const desc  = (lang === 'en' && ev.description_en) ? ev.description_en : ev.description;

  return (
    <div style={{
      background: '#fff', borderRadius: 14, marginBottom: 12,
      border: `1.5px solid ${isPast ? '#e5e7eb' : typeColor + '40'}`,
      overflow: 'hidden', opacity: isPast ? 0.72 : 1,
      boxShadow: isPast ? 'none' : `0 3px 12px ${typeColor}18`,
    }}>
      {/* Banner image */}
      {ev.banner_image && (
        <img src={ev.banner_image} alt={title} style={{ width: '100%', maxHeight: 160, objectFit: 'cover', display: 'block' }} />
      )}

      <div style={{ padding: '12px 14px' }}>
        {/* Type + urgency badges */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 800, background: typeBg, color: typeColor, border: `1px solid ${typeColor}30`, borderRadius: 20, padding: '2px 8px' }}>
            {typeIcon} {typeLabel}
          </span>
          {isToday   && <span style={{ fontSize: 10, fontWeight: 800, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 20, padding: '2px 8px' }}>🔔 {t('आज', 'TODAY')}</span>}
          {isTomorrow&& <span style={{ fontSize: 10, fontWeight: 800, background: '#ecfdf5', color: '#065f46', border: '1px solid #34d399', borderRadius: 20, padding: '2px 8px' }}>⏰ {t('कल', 'TOMORROW')}</span>}
          {isSoon && !isToday && !isTomorrow && <span style={{ fontSize: 10, fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', borderRadius: 20, padding: '2px 8px' }}>{daysAway} {t('दिन बाद', 'days away')}</span>}
          {isPast    && <span style={{ fontSize: 10, color: '#9ca3af', borderRadius: 20, padding: '2px 8px', background: '#f9fafb' }}>{t('समाप्त', 'Past')}</span>}
          {pm.icon && <span style={{ fontSize: 10, fontWeight: 700, background: pm.color + '15', color: pm.color, borderRadius: 20, padding: '2px 8px', border: `1px solid ${pm.color}30` }}>{pm.icon} {pm.label}</span>}
        </div>

        {/* Title */}
        <div style={{ fontSize: 15, fontWeight: 900, color: '#111827', lineHeight: 1.3, marginBottom: 6 }}>{title}</div>

        {/* Date/time — timezone aware */}
        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 5 }}>
          <span>📅</span>
          <span style={{ lineHeight: 1.5 }}>{fmtEventDate(ev.event_date, ev.event_time, userTz)}</span>
        </div>

        {/* Location / Virtual info */}
        {(isPhysical || isHybrid) && ev.location && (
          <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 6, display: 'flex', gap: 5 }}>
            <span>📍</span><span>{ev.location}</span>
          </div>
        )}

        {/* Description */}
        {desc && <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>{desc}</div>}

        {/* Virtual join button */}
        {(isVirtual || isHybrid) && ev.virtual_link && !isPast && (
          <a href={ev.virtual_link} target="_blank" rel="noopener noreferrer" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            background: pm.color || typeColor, color: '#fff', borderRadius: 10,
            padding: '10px 0', fontSize: 13, fontWeight: 800, textDecoration: 'none',
            boxShadow: `0 3px 10px ${(pm.color || typeColor)}40`,
          }}>
            {pm.icon || '🌐'} {t('अभी Join करें', 'Join Now')} — {pm.label || t('ऑनलाइन', 'Online')}
          </a>
        )}

        {/* Share event */}
        {!isPast && (
          <button onClick={() => {
            const text = `${title}\n📅 ${fmtEventDate(ev.event_date, ev.event_time, null)}\n${ev.virtual_link ? `🌐 ${ev.virtual_link}` : (ev.location ? `📍 ${ev.location}` : '')}`;
            if (navigator.share) navigator.share({ title, text }).catch(() => {});
            else navigator.clipboard?.writeText(text);
          }} style={{ marginTop: 8, width: '100%', background: 'none', border: `1px solid ${typeColor}40`, borderRadius: 10, padding: '7px 0', fontSize: 11, fontWeight: 700, color: typeColor, cursor: 'pointer' }}>
            🔗 {t('Share करें', 'Share Event')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Gotra Directory Tab ───────────────────────────────────────────────────────
export function GotraDirectoryTab({ profile }) {
  const { t, lang } = useLang();

  const [view,          setView]          = useState('browse');
  const [innerTab,      setInnerTab]      = useState('gotras');
  const [search,        setSearch]        = useState('');
  const [gotras,        setGotras]        = useState([]);
  const [kuldevis,      setKuldevis]      = useState([]);
  const [kulrishis,     setKulrishis]     = useState([]);
  const [selected,      setSelected]      = useState(null);
  const [detail,        setDetail]        = useState(null);
  const [kuldeviDetail, setKuldeviDetail] = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [detailLoad,    setDetailLoad]    = useState(false);

  const [cType,       setCType]       = useState('correction');
  const [cAboutGotra, setCAboutGotra] = useState('');
  const [cData,       setCData]       = useState({});
  const [cVillage,    setCVillage]    = useState(profile?.village_name || '');
  const [cGotra,      setCGotra]      = useState(profile?.gotra || '');
  const [cSubmitting, setCSubmitting] = useState(false);
  const [cDone,       setCDone]       = useState(false);

  const [commentText, setCommentText] = useState('');
  const [commVillage, setCommVillage] = useState(profile?.village_name || '');
  const [commGotra,   setCommGotra]   = useState(profile?.gotra || '');
  const [commDone,    setCommDone]    = useState(false);
  const [commSaving,  setCommSaving]  = useState(false);

  const fetchGotras = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50, ...(q ? { search: q } : {}) });
      const r = await axios.get(`${baseUrl}/member/gotra-directory/gotras?${params}`, { headers: authHeader() });
      if (r.data.status) setGotras(r.data.result || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchKuldevis = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 50, ...(q ? { search: q } : {}) });
      const r = await axios.get(`${baseUrl}/member/gotra-directory/kuldevis?${params}`, { headers: authHeader() });
      if (r.data.status) setKuldevis(r.data.result || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const fetchKulrishis = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 1, limit: 100, ...(q ? { search: q } : {}) });
      const r = await axios.get(`${baseUrl}/member/gotra-directory/kulrishis?${params}`, { headers: authHeader() });
      if (r.data.status) setKulrishis(r.data.result || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (innerTab === 'gotras') fetchGotras(search);
    else if (innerTab === 'kuldevis') fetchKuldevis(search);
    else fetchKulrishis(search);
  }, [innerTab]);

  const handleSearch = (val) => {
    setSearch(val);
    if (innerTab === 'gotras') fetchGotras(val);
    else if (innerTab === 'kuldevis') fetchKuldevis(val);
    else fetchKulrishis(val);
  };

  const openKuldeviDetail = (k) => { setKuldeviDetail(k); setView('kuldevi_detail'); };

  const openDetail = async (item) => {
    setSelected(item); setDetail(null); setView('detail');
    setDetailLoad(true); setCommentText(''); setCommDone(false);
    try {
      const r = await axios.get(`${baseUrl}/member/gotra-directory/gotras/${item.id}`, { headers: authHeader() });
      if (r.data.status) setDetail(r.data.result);
    } catch {} finally { setDetailLoad(false); }
  };

  const submitComment = async () => {
    if (!commentText.trim() || commentText.trim().length < 10) {
      notification.warning({ message: t('टिप्पणी कम से कम 10 अक्षर होनी चाहिए','Comment must be at least 10 characters'), placement:'topRight' });
      return;
    }
    setCommSaving(true);
    try {
      const r = await axios.post(`${baseUrl}/member/gotra-directory/comment`, {
        entity_type: 'gotra', entity_id: selected.id,
        comment_text: commentText, commenter_village: commVillage, commenter_gotra: commGotra,
      }, { headers: authHeader() });
      if (r.data.status) { setCommDone(true); setCommentText(''); }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || t('त्रुटि','Error'), placement:'topRight' });
    } finally { setCommSaving(false); }
  };

  const submitContribution = async () => {
    if (!Object.values(cData).some(v => v?.toString().trim())) {
      notification.warning({ message: t('कृपया जानकारी भरें','Please fill in the information'), placement:'topRight' });
      return;
    }
    if (cType === 'correction' && !cAboutGotra?.trim()) {
      notification.warning({ message: t('किस गोत्र की जानकारी सुधारनी है — यह जरूरी है','Please specify which gotra this correction is for'), placement:'topRight' });
      return;
    }
    if (cType === 'new_gotra' && !cData.gotra_name?.trim()) {
      notification.warning({ message: t('नए गोत्र का नाम जरूरी है','New gotra name is required'), placement:'topRight' });
      return;
    }
    if (cType === 'new_kuldevi' && !cData.kuldevi_name?.trim()) {
      notification.warning({ message: t('कुलदेवी का नाम जरूरी है','Kuldevi name is required'), placement:'topRight' });
      return;
    }
    setCSubmitting(true);
    try {
      const r = await axios.post(`${baseUrl}/member/gotra-directory/contribute`, {
        contribution_type: cType, about_gotra: cAboutGotra || undefined,
        submitted_data: cData, submitter_village: cVillage, submitter_gotra: cGotra,
      }, { headers: authHeader() });
      if (r.data.status) { setCDone(true); }
    } catch (err) {
      notification.error({ message: err?.response?.data?.message || t('त्रुटि','Error'), placement:'topRight' });
    } finally { setCSubmitting(false); }
  };

  const cardStyle = {
    background: '#fff', borderRadius: 14, border: `1px solid ${M}15`,
    boxShadow: '0 2px 12px rgba(122,0,0,0.06)', marginBottom: 12,
    cursor: 'pointer', transition: 'box-shadow 0.15s',
  };

  // ── CONTRIBUTE VIEW ──────────────────────────────────────────────────────────
  if (view === 'contribute') {
    const CTYPES = [
      { key: 'correction',    labelHi: 'जानकारी सुधार',       labelEn: 'Correct Information' },
      { key: 'new_gotra',     labelHi: 'नया गोत्र जोड़ें',     labelEn: 'Add New Gotra' },
      { key: 'new_kuldevi',   labelHi: 'नई कुलदेवी जोड़ें',    labelEn: 'Add New Kuldevi' },
      { key: 'link_info',     labelHi: 'गोत्र-कुलदेवी लिंक',  labelEn: 'Gotra-Kuldevi Link' },
      { key: 'kulrishi_info', labelHi: 'कुलऋषि जानकारी',       labelEn: 'Kulrishi Info' },
      { key: 'general_info',  labelHi: 'अन्य जानकारी',          labelEn: 'Other Info' },
    ];

    if (cDone) return (
      <div>
        <button onClick={() => { setView('browse'); setCDone(false); setCData({}); }}
          style={{ background:'none',border:'none',cursor:'pointer',color:M,fontWeight:700,fontSize:13,marginBottom:20,display:'flex',alignItems:'center',gap:6 }}>
          ← {t('वापस','Back')}
        </button>
        <div style={{ background:'#fff',borderRadius:16,padding:'32px 20px',textAlign:'center',boxShadow:'0 4px 20px rgba(122,0,0,0.08)' }}>
          <div style={{ fontSize:48,marginBottom:12 }}>🙏</div>
          <div style={{ fontSize:18,fontWeight:900,color:M,marginBottom:8 }}>{t('धन्यवाद!','Thank You!')}</div>
          <div style={{ fontSize:13,color:'#6b7280',lineHeight:1.8 }}>
            {t('आपका योगदान सफलतापूर्वक जमा हो गया। समीक्षा के बाद निर्देशिका में जोड़ा जाएगा।',
               'Your contribution has been submitted. It will be added to the directory after review.')}
          </div>
          <button onClick={() => { setView('browse'); setCDone(false); setCData({}); }}
            style={{ marginTop:20,background:M,color:'#fff',border:'none',borderRadius:10,padding:'10px 28px',fontSize:13,fontWeight:700,cursor:'pointer' }}>
            {t('निर्देशिका देखें','View Directory')}
          </button>
        </div>
      </div>
    );

    return (
      <div>
        <button onClick={() => setView('browse')}
          style={{ background:'none',border:'none',cursor:'pointer',color:M,fontWeight:700,fontSize:13,marginBottom:16,display:'flex',alignItems:'center',gap:6 }}>
          ← {t('वापस','Back')}
        </button>
        <div style={{ background:'#fff',borderRadius:16,overflow:'hidden',boxShadow:'0 4px 20px rgba(122,0,0,0.08)' }}>
          {/* Header — maroon gradient matching portal */}
          <div style={{ background:`linear-gradient(135deg,${M},${ML})`,padding:'16px 18px' }}>
            <div style={{ fontSize:18,fontWeight:900,color:'#fff' }}>📖 {t('योगदान करें','Contribute')}</div>
            <div style={{ fontSize:12,color:'rgba(255,255,255,0.8)',marginTop:4 }}>
              {t('आपकी जानकारी समाज की धरोहर बनेगी','Your knowledge will become community heritage')}
            </div>
          </div>

          <div style={{ padding:'18px 16px' }}>
            {/* Contribution type pills */}
            <div style={{ marginBottom:14 }}>
              <div style={{ fontSize:12,fontWeight:700,color:'#374151',marginBottom:8 }}>{t('योगदान का प्रकार','Type of Contribution')}</div>
              <div style={{ display:'flex',flexWrap:'wrap',gap:7 }}>
                {CTYPES.map(ct => (
                  <button key={ct.key} onClick={() => { setCType(ct.key); setCData({}); }}
                    style={{ background: cType === ct.key ? M : `${M}08`,
                      color: cType === ct.key ? '#fff' : M,
                      border: `1.5px solid ${cType === ct.key ? M : `${M}30`}`,
                      borderRadius:20, padding:'5px 13px', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                    {lang === 'en' ? ct.labelEn : ct.labelHi}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:12,fontWeight:700,color:'#374151',marginBottom:5 }}>
                {t('किस गोत्र / कुलदेवी के बारे में?','About which Gotra / Kuldevi?')}
              </div>
              <input value={cAboutGotra} onChange={e => setCAboutGotra(e.target.value)}
                style={{ width:'100%',padding:'8px 12px',border:`1.5px solid ${M}30`,borderRadius:8,fontSize:13,boxSizing:'border-box' }}
                placeholder={t('गोत्र / कुलदेवी का नाम...','Gotra / Kuldevi name...')} />
            </div>

            {cType === 'new_gotra'  && <GotraContributeFields   data={cData} setData={setCData} t={t} lang={lang} />}
            {cType === 'new_kuldevi'&& <KuldeviContributeFields data={cData} setData={setCData} t={t} lang={lang} />}
            {!['new_gotra','new_kuldevi'].includes(cType) && (
              <div style={{ marginBottom:12 }}>
                <div style={{ fontSize:12,fontWeight:700,color:'#374151',marginBottom:5 }}>{t('आपकी जानकारी','Your Information')}</div>
                <textarea value={cData.info||''} onChange={e => setCData(d => ({ ...d, info:e.target.value }))} rows={4}
                  style={{ width:'100%',padding:'8px 12px',border:`1.5px solid ${M}25`,borderRadius:8,fontSize:13,boxSizing:'border-box',resize:'vertical' }}
                  placeholder={t('यहाँ विस्तार से लिखें... जितनी भी जानकारी हो साझा करें।','Write in detail... share whatever information you have.')} />
              </div>
            )}

            {/* Your details */}
            <div style={{ background:`${M}05`,borderRadius:10,padding:'12px 14px',marginTop:4,marginBottom:14,border:`1px solid ${M}15` }}>
              <div style={{ fontSize:11,fontWeight:700,color:'#6b7280',marginBottom:8,textTransform:'uppercase',letterSpacing:0.4 }}>
                {t('आपके बारे में (वैकल्पिक)','About You (optional)')}
              </div>
              <div style={{ display:'flex',gap:8 }}>
                <input value={cVillage} onChange={e => setCVillage(e.target.value)}
                  style={{ flex:1,padding:'7px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:12 }}
                  placeholder={t('आपका गाँव','Your Village')} />
                <input value={cGotra} onChange={e => setCGotra(e.target.value)}
                  style={{ flex:1,padding:'7px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:12 }}
                  placeholder={t('आपका गोत्र','Your Gotra')} />
              </div>
            </div>

            <button onClick={submitContribution} disabled={cSubmitting}
              style={{ width:'100%',background:M,color:'#fff',border:'none',borderRadius:10,padding:'12px',fontSize:14,fontWeight:800,cursor:'pointer',opacity:cSubmitting?0.7:1 }}>
              {cSubmitting ? t('जमा हो रहा है...','Submitting...') : `🙏 ${t('योगदान जमा करें','Submit Contribution')}`}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const infoItems = detail ? [
      { labelHi:'प्रवर (ऋषि)',      labelEn:'Pravara',           value: detail.pravara },
      { labelHi:'प्रवर संख्या',     labelEn:'Pravara Count',     value: detail.pravara_count },
      { labelHi:'वेद',              labelEn:'Veda',              value: detail.veda },
      { labelHi:'वैदिक शाखा',       labelEn:'Vedic Shakha',      value: detail.vedic_shakha },
      { labelHi:'गोत्र प्रवर्तक',   labelEn:'Gotra Pravartaka',  value: detail.gotra_pravartaka },
      { labelHi:'कुलऋषि',           labelEn:'Kulrishi',          value: detail.kulrishi },
      { labelHi:'कुलदेवी',          labelEn:'Kuldevi',           value: detail.kuldevi_text },
      { labelHi:'भौगोलिक उत्पत्ति', labelEn:'Geographical Origin', value: detail.geographical_origin },
      { labelHi:'संबंधित उपनाम',    labelEn:'Associated Surnames', value: detail.surnames_associated },
    ].filter(i => i.value) : [];

    return (
      <div>
        <button onClick={() => { setView('browse'); setSelected(null); setDetail(null); }}
          style={{ background:'none',border:'none',cursor:'pointer',color:M,fontWeight:700,fontSize:13,marginBottom:16,display:'flex',alignItems:'center',gap:6 }}>
          ← {t('वापस','Back')}
        </button>

        {/* Header — portal maroon */}
        <div style={{ background:`linear-gradient(135deg,${M},${ML})`,borderRadius:14,padding:'18px 18px 16px',marginBottom:14 }}>
          <div style={{ display:'flex',alignItems:'center',gap:12 }}>
            <div style={{ width:50,height:50,borderRadius:14,background:'rgba(255,255,255,0.18)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:24,flexShrink:0 }}>🏛️</div>
            <div>
              <div style={{ fontSize:20,fontWeight:900,color:'#fff' }}>{selected.name}</div>
              {selected.name_en && <div style={{ fontSize:13,color:'rgba(255,255,255,0.75)',fontWeight:500 }}>{selected.name_en} Gotra</div>}
              {selected.is_verified ? <span style={{ fontSize:10,background:'rgba(255,215,0,0.25)',color:'#ffd700',borderRadius:20,padding:'2px 9px',fontWeight:800 }}>✓ {t('सत्यापित','Verified')}</span> : null}
            </div>
          </div>
        </div>

        {detailLoad ? (
          <div style={{ textAlign:'center',padding:32,color:M,fontSize:24 }}>⏳</div>
        ) : detail ? (
          <>
            {infoItems.length > 0 && (
              <div style={{ background:'#fff',borderRadius:14,overflow:'hidden',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
                {infoItems.map((item, i) => (
                  <div key={i} style={{ display:'flex',borderBottom: i < infoItems.length-1 ? '1px solid #f3f4f6':'none',padding:'10px 14px' }}>
                    <div style={{ fontSize:11,fontWeight:700,color:'#9ca3af',width:140,flexShrink:0,paddingTop:1 }}>
                      {lang==='en' ? item.labelEn : item.labelHi}
                    </div>
                    <div style={{ fontSize:13,color:'#1f2937',fontWeight:500,flex:1 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            )}

            {(detail.description||detail.description_en) && (
              <div style={{ background:'#fff',borderRadius:14,padding:'14px 16px',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:8,textTransform:'uppercase',letterSpacing:0.5 }}>
                  {t('विवरण','Description')}
                </div>
                <div style={{ fontSize:13,color:'#374151',lineHeight:1.9,whiteSpace:'pre-wrap' }}>
                  {lang==='en' && detail.description_en ? detail.description_en : detail.description}
                </div>
              </div>
            )}

            {detail.kuldevis?.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13,fontWeight:800,color:'#374151',marginBottom:8 }}>🛕 {t('कुलदेवी','Kuldevis')}</div>
                {detail.kuldevis.map(k => (
                  <div key={k.id} style={{ background:'#fff',borderRadius:12,padding:'12px 14px',marginBottom:8,
                    border:`1.5px solid ${k.is_primary ? M : '#e5e7eb'}`,boxShadow:'0 2px 8px rgba(0,0,0,0.04)' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:800,fontSize:14,color:'#1f2937' }}>{k.name}</div>
                        {k.name_en && <div style={{ fontSize:11,color:'#6b7280' }}>{k.name_en}</div>}
                      </div>
                      {k.is_primary ? <span style={{ fontSize:10,background:`${M}10`,color:M,borderRadius:20,padding:'2px 9px',fontWeight:800 }}>🌟 {t('प्रमुख','Primary')}</span> : null}
                    </div>
                    {k.temple_city && <div style={{ fontSize:11,color:'#6b7280',marginTop:4 }}>📍 {[k.temple_city,k.temple_state].filter(Boolean).join(', ')}</div>}
                    {k.festival_info && <div style={{ fontSize:12,color:'#374151',marginTop:6,lineHeight:1.7 }}>{k.festival_info}</div>}
                  </div>
                ))}
              </div>
            )}

            {detail.mythological_reference && (
              <div style={{ background:'#fff8f0',borderRadius:12,padding:'12px 14px',marginBottom:14,border:`1px solid ${M}25` }}>
                <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:6 }}>📜 {t('पौराणिक संदर्भ','Mythological Reference')}</div>
                <div style={{ fontSize:12,color:'#374151',lineHeight:1.8 }}>{detail.mythological_reference}</div>
              </div>
            )}

            {detail.comments?.length > 0 && (
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13,fontWeight:800,color:'#374151',marginBottom:8 }}>💬 {t('समुदाय की टिप्पणियाँ','Community Comments')}</div>
                {detail.comments.map((c, i) => (
                  <div key={i} style={{ background:'#fff',borderRadius:10,padding:'10px 13px',marginBottom:7,boxShadow:'0 1px 6px rgba(0,0,0,0.05)' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',marginBottom:4 }}>
                      <div style={{ fontSize:12,fontWeight:700,color:M }}>{c.commenter_name}</div>
                      <div style={{ fontSize:10,color:'#9ca3af' }}>{c.created_at?.slice(0,10)}</div>
                    </div>
                    {(c.commenter_village||c.commenter_gotra) && (
                      <div style={{ fontSize:10,color:'#9ca3af',marginBottom:5 }}>
                        {[c.commenter_village, c.commenter_gotra ? `गोत्र: ${c.commenter_gotra}` : null].filter(Boolean).join(' · ')}
                      </div>
                    )}
                    <div style={{ fontSize:13,color:'#374151',lineHeight:1.7 }}>{c.comment_text}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Comment form */}
            <div style={{ background:'#fff',borderRadius:14,padding:'14px 16px',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize:12,fontWeight:700,color:'#374151',marginBottom:8 }}>✍️ {t('टिप्पणी जोड़ें','Add Comment')}</div>
              {commDone ? (
                <div style={{ color:'#16a34a',fontSize:13,fontWeight:700,textAlign:'center',padding:'8px 0' }}>
                  🙏 {t('टिप्पणी जमा हुई! समीक्षा बाद दिखेगी।','Comment submitted! Will appear after review.')}
                </div>
              ) : (
                <>
                  <div style={{ display:'flex',gap:7,marginBottom:8 }}>
                    <input value={commVillage} onChange={e => setCommVillage(e.target.value)}
                      style={{ flex:1,padding:'6px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:11 }}
                      placeholder={t('आपका गाँव','Your Village')} />
                    <input value={commGotra} onChange={e => setCommGotra(e.target.value)}
                      style={{ flex:1,padding:'6px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:11 }}
                      placeholder={t('आपका गोत्र','Your Gotra')} />
                  </div>
                  <textarea value={commentText} onChange={e => setCommentText(e.target.value)} rows={3}
                    style={{ width:'100%',padding:'8px 12px',border:`1.5px solid ${M}25`,borderRadius:8,fontSize:12,boxSizing:'border-box',resize:'vertical' }}
                    placeholder={t('इस गोत्र के बारे में आपकी जानकारी या अनुभव साझा करें...','Share your knowledge or experience about this gotra...')} />
                  <button onClick={submitComment} disabled={commSaving}
                    style={{ marginTop:8,width:'100%',background:M,color:'#fff',border:'none',borderRadius:8,padding:'9px',fontSize:13,fontWeight:700,cursor:'pointer',opacity:commSaving?0.7:1 }}>
                    {commSaving ? t('जमा हो रहा है...','Submitting...') : t('टिप्पणी जमा करें','Submit Comment')}
                  </button>
                </>
              )}
            </div>
          </>
        ) : null}

        <button onClick={() => { setCAboutGotra(selected.name); setView('contribute'); }}
          style={{ width:'100%',background:`${M}08`,color:M,border:`2px dashed ${M}40`,borderRadius:12,padding:'12px',fontSize:13,fontWeight:800,cursor:'pointer',marginBottom:8 }}>
          ➕ {t('इस गोत्र के बारे में जानकारी जोड़ें','Add Information About This Gotra')}
        </button>
      </div>
    );
  }

  // ── KULDEVI DETAIL VIEW ──────────────────────────────────────────────────────
  if (view === 'kuldevi_detail' && kuldeviDetail) {
    const k = kuldeviDetail;
    const fields = [
      { labelHi:'मंदिर पता', labelEn:'Temple Address', value: k.temple_location },
      { labelHi:'शहर/गाँव',  labelEn:'City/Village',   value: k.temple_city },
      { labelHi:'जिला',      labelEn:'District',        value: k.temple_district },
      { labelHi:'राज्य',     labelEn:'State',           value: k.temple_state },
      { labelHi:'देवी प्रकार',labelEn:'Deity Type',     value: k.deity_type },
      { labelHi:'अन्य नाम',  labelEn:'Also Known As',   value: k.also_known_as },
    ].filter(i => i.value);
    return (
      <div>
        <button onClick={() => { setView('browse'); setKuldeviDetail(null); }}
          style={{ background:'none',border:'none',cursor:'pointer',color:M,fontWeight:700,fontSize:13,marginBottom:16,display:'flex',alignItems:'center',gap:6 }}>
          ← {t('वापस','Back')}
        </button>
        {/* Photo */}
        {k.photo_url && (
          <img src={k.photo_url} alt={k.name}
            style={{ width:'100%',maxHeight:240,objectFit:'cover',borderRadius:14,marginBottom:12 }} />
        )}
        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,${M},${ML})`,borderRadius:14,padding:'16px 18px',marginBottom:14 }}>
          <div style={{ fontSize:22,fontWeight:900,color:'#fff' }}>🛕 {k.name}</div>
          {k.name_en && <div style={{ fontSize:13,color:'rgba(255,255,255,0.75)' }}>{k.name_en}</div>}
          {k.is_verified ? <span style={{ fontSize:10,background:'rgba(255,215,0,0.25)',color:'#ffd700',borderRadius:20,padding:'2px 9px',fontWeight:800,marginTop:6,display:'inline-block' }}>✓ {t('सत्यापित','Verified')}</span> : null}
        </div>
        {/* Info rows */}
        {fields.length > 0 && (
          <div style={{ background:'#fff',borderRadius:14,overflow:'hidden',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
            {fields.map((item, i) => (
              <div key={i} style={{ display:'flex',borderBottom: i < fields.length-1 ? '1px solid #f3f4f6':'none',padding:'10px 14px' }}>
                <div style={{ fontSize:11,fontWeight:700,color:'#9ca3af',width:130,flexShrink:0,paddingTop:1 }}>
                  {lang==='en' ? item.labelEn : item.labelHi}
                </div>
                <div style={{ fontSize:13,color:'#1f2937',fontWeight:500,flex:1 }}>{item.value}</div>
              </div>
            ))}
          </div>
        )}
        {k.deity_description && (
          <div style={{ background:'#fff',borderRadius:14,padding:'14px 16px',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:6,textTransform:'uppercase',letterSpacing:0.5 }}>{t('देवी परिचय','About the Deity')}</div>
            <div style={{ fontSize:13,color:'#374151',lineHeight:1.9 }}>{k.deity_description}</div>
          </div>
        )}
        {k.festival_info && (
          <div style={{ background:'#fff',borderRadius:14,padding:'14px 16px',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:6,textTransform:'uppercase',letterSpacing:0.5 }}>🎉 {t('पूजा / उत्सव','Festivals & Worship')}</div>
            <div style={{ fontSize:13,color:'#374151',lineHeight:1.9 }}>{k.festival_info}</div>
          </div>
        )}
        {k.mythological_story && (
          <div style={{ background:'#fff8f0',borderRadius:14,padding:'14px 16px',marginBottom:14,border:`1px solid ${M}25` }}>
            <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:6 }}>📜 {t('पौराणिक कथा','Mythological Story')}</div>
            <div style={{ fontSize:12,color:'#374151',lineHeight:1.8 }}>{k.mythological_story}</div>
          </div>
        )}
        {k.historical_reference && (
          <div style={{ background:'#fff',borderRadius:14,padding:'14px 16px',marginBottom:14,boxShadow:'0 2px 10px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:11,fontWeight:700,color:M,marginBottom:6 }}>📚 {t('ऐतिहासिक संदर्भ','Historical Reference')}</div>
            <div style={{ fontSize:12,color:'#374151',lineHeight:1.8 }}>{k.historical_reference}</div>
          </div>
        )}
        <button onClick={() => { setCAboutGotra(k.name); setCData({}); setCType('new_kuldevi'); setCDone(false); setView('contribute'); }}
          style={{ width:'100%',background:`${M}08`,color:M,border:`2px dashed ${M}40`,borderRadius:12,padding:'12px',fontSize:13,fontWeight:800,cursor:'pointer' }}>
          ➕ {t('इस कुलदेवी के बारे में जानकारी जोड़ें','Add Information About This Kuldevi')}
        </button>
      </div>
    );
  }

  // ── BROWSE VIEW ──────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header Banner — matches portal maroon theme */}
      <div style={{ background:`linear-gradient(135deg,${M},${ML})`,borderRadius:16,padding:'18px 18px 16px',marginBottom:16 }}>
        <div style={{ fontSize:20,fontWeight:900,color:'#fff',marginBottom:4 }}>🏛️ {t('गोत्र निर्देशिका','Gotra Directory')}</div>
        <div style={{ fontSize:12,color:'rgba(255,255,255,0.8)',lineHeight:1.6 }}>
          {t('गोत्र, कुलदेवी, कुलऋषि की बहुआयामी सामुदायिक जानकारी। आप भी योगदान करें।',
             'Multi-dimensional community knowledge of Gotra, Kuldevi, Kulrishi. Contribute your knowledge.')}
        </div>
        <button onClick={() => { setCAboutGotra(''); setCData({}); setCType('new_gotra'); setCDone(false); setView('contribute'); }}
          style={{ marginTop:10,background:'rgba(255,255,255,0.2)',color:'#fff',border:'1.5px solid rgba(255,255,255,0.5)',borderRadius:20,padding:'6px 16px',fontSize:12,fontWeight:800,cursor:'pointer' }}>
          + {t('योगदान करें','Contribute')}
        </button>
      </div>

      {/* Search */}
      <div style={{ marginBottom:14 }}>
        <input value={search} onChange={e => handleSearch(e.target.value)}
          style={{ width:'100%',padding:'9px 13px',border:`1.5px solid ${M}25`,borderRadius:10,fontSize:13,boxSizing:'border-box' }}
          placeholder={innerTab==='gotras' ? t('गोत्र खोजें... (नाम, प्रवर, उपनाम)','Search gotra...') : t('कुलदेवी खोजें...','Search kuldevi...')} />
      </div>

      {/* Inner tabs — 3-way segmented control */}
      <div style={{ display:'flex',gap:0,marginBottom:16,background:'#f3f4f6',borderRadius:10,padding:3 }}>
        {[
          { key:'gotras',   labelHi:'🏛️ गोत्र',   labelEn:'🏛️ Gotras' },
          { key:'kuldevis', labelHi:'🛕 कुलदेवी', labelEn:'🛕 Kuldevis' },
          { key:'kulrishis',labelHi:'📿 कुलऋषि',  labelEn:'📿 Kulrishis' },
        ].map(tab => (
          <button key={tab.key} onClick={() => { setInnerTab(tab.key); setSearch(''); }}
            style={{ flex:1,border:'none',borderRadius:8,padding:'8px 4px',fontSize:11,fontWeight:700,cursor:'pointer',
              background: innerTab===tab.key ? '#fff' : 'transparent',
              color: innerTab===tab.key ? M : '#6b7280',
              boxShadow: innerTab===tab.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none' }}>
            {lang==='en' ? tab.labelEn : tab.labelHi}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign:'center',padding:32,color:M,fontSize:24 }}>⏳</div>
      ) : innerTab==='gotras' ? (
        gotras.length===0 ? (
          <div style={{ textAlign:'center',padding:32,color:'#9ca3af' }}>
            {search ? t('कोई गोत्र नहीं मिला','No gotra found') : t('अभी कोई गोत्र नहीं जोड़ा गया','No gotras added yet')}
          </div>
        ) : (
          gotras.map(g => (
            <div key={g.id} style={cardStyle} onClick={() => openDetail(g)}>
              <div style={{ padding:'14px 16px',display:'flex',alignItems:'center',gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:12,background:`${M}12`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0 }}>🏛️</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:15,fontWeight:900,color:'#1f2937' }}>{g.name}
                    {g.name_en ? <span style={{ fontSize:11,fontWeight:500,color:'#6b7280',marginLeft:8 }}>{g.name_en}</span> : null}
                  </div>
                  {g.pravara && (
                    <div style={{ fontSize:11,color:'#6b7280',marginTop:2 }}>
                      <span style={{ fontWeight:700,color:'#9ca3af',fontSize:9,textTransform:'uppercase' }}>{t('प्रवर','Pravara')}: </span>{g.pravara}
                    </div>
                  )}
                  <div style={{ display:'flex',gap:5,flexWrap:'wrap',marginTop:6 }}>
                    {g.veda && <span style={{ fontSize:9,background:'#eff6ff',color:'#2563eb',borderRadius:20,padding:'2px 8px',fontWeight:700 }}>{g.veda}</span>}
                    {g.kuldevi_text && <span style={{ fontSize:9,background:`${M}10`,color:M,borderRadius:20,padding:'2px 8px',fontWeight:700 }}>🛕 {g.kuldevi_text}</span>}
                    {g.geographical_origin && <span style={{ fontSize:9,background:'#f0fdf4',color:'#16a34a',borderRadius:20,padding:'2px 8px',fontWeight:700 }}>📍 {g.geographical_origin}</span>}
                    {g.is_verified ? <span style={{ fontSize:9,background:'#fefce8',color:'#ca8a04',borderRadius:20,padding:'2px 8px',fontWeight:700 }}>✓ {t('सत्यापित','Verified')}</span> : null}
                  </div>
                </div>
                <span style={{ fontSize:18,color:'#d1d5db' }}>›</span>
              </div>
            </div>
          ))
        )
      ) : innerTab==='kuldevis' ? (
        kuldevis.length===0 ? (
          <div style={{ textAlign:'center',padding:32,color:'#9ca3af' }}>
            {search ? t('कोई कुलदेवी नहीं मिली','No kuldevi found') : t('अभी कोई कुलदेवी नहीं जोड़ी गई','No kuldevis added yet')}
          </div>
        ) : (
          kuldevis.map(k => (
            <div key={k.id} style={{...cardStyle,cursor:'pointer'}} onClick={() => openKuldeviDetail(k)}>
              <div style={{ display:'flex',alignItems:'center',gap:0,overflow:'hidden',borderRadius:14 }}>
                {/* Photo or fallback */}
                {k.photo_url
                  ? <img src={k.photo_url} alt={k.name} style={{ width:80,height:80,objectFit:'cover',flexShrink:0 }} />
                  : <div style={{ width:80,height:80,background:`${M}10`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:30,flexShrink:0 }}>🛕</div>
                }
                <div style={{ flex:1,padding:'10px 12px',minWidth:0 }}>
                  <div style={{ fontSize:14,fontWeight:900,color:'#1f2937' }}>{k.name}
                    {k.name_en ? <span style={{ fontSize:11,fontWeight:500,color:'#6b7280',marginLeft:6 }}>{k.name_en}</span> : null}
                  </div>
                  {k.also_known_as && <div style={{ fontSize:10,color:'#9ca3af',marginTop:1 }}>{k.also_known_as}</div>}
                  <div style={{ display:'flex',gap:5,flexWrap:'wrap',marginTop:5 }}>
                    {(k.temple_city||k.temple_state) && <span style={{ fontSize:9,background:'#f0fdf4',color:'#16a34a',borderRadius:20,padding:'2px 8px',fontWeight:700 }}>📍 {[k.temple_city,k.temple_state].filter(Boolean).join(', ')}</span>}
                    {k.deity_type && <span style={{ fontSize:9,background:`${M}10`,color:M,borderRadius:20,padding:'2px 8px',fontWeight:700 }}>{k.deity_type}</span>}
                    {k.is_verified ? <span style={{ fontSize:9,background:'#fefce8',color:'#ca8a04',borderRadius:20,padding:'2px 8px',fontWeight:700 }}>✓ {t('सत्यापित','Verified')}</span> : null}
                  </div>
                  {k.festival_info && <div style={{ fontSize:10,color:'#6b7280',marginTop:4,lineHeight:1.5 }}>{k.festival_info.slice(0,80)}{k.festival_info.length>80?'...':''}</div>}
                </div>
                <span style={{ fontSize:16,color:'#d1d5db',paddingRight:10,flexShrink:0 }}>›</span>
              </div>
            </div>
          ))
        )
      ) : (
        kulrishis.length===0 ? (
          <div style={{ textAlign:'center',padding:32,color:'#9ca3af' }}>
            {search ? t('कोई कुलऋषि नहीं मिला','No kulrishi found') : t('अभी कोई कुलऋषि नहीं जोड़ा गया','No kulrishis added yet')}
          </div>
        ) : (
          kulrishis.map(kr => (
            <div key={kr.id} style={cardStyle}>
              <div style={{ padding:'14px 16px',display:'flex',alignItems:'flex-start',gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:12,background:'#fef3c7',display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0 }}>📿</div>
                <div style={{ flex:1,minWidth:0 }}>
                  <div style={{ fontSize:15,fontWeight:900,color:'#1f2937' }}>{kr.name}
                    {kr.name_en ? <span style={{ fontSize:11,fontWeight:500,color:'#6b7280',marginLeft:8 }}>{kr.name_en}</span> : null}
                  </div>
                  {kr.veda && <div style={{ fontSize:10,color:'#2563eb',fontWeight:700,marginTop:2 }}>{kr.veda}</div>}
                  {kr.linked_gotras_text && (
                    <div style={{ fontSize:11,color:'#6b7280',marginTop:3 }}>
                      <span style={{ fontWeight:700,color:'#9ca3af',fontSize:9,textTransform:'uppercase' }}>{t('गोत्र','Gotras')}: </span>{kr.linked_gotras_text}
                    </div>
                  )}
                  {kr.lineage_description && <div style={{ fontSize:11,color:'#374151',marginTop:5,lineHeight:1.6 }}>{kr.lineage_description.slice(0,120)}{kr.lineage_description.length>120?'...':''}</div>}
                  {kr.is_verified ? <span style={{ fontSize:9,background:'#fefce8',color:'#ca8a04',borderRadius:20,padding:'2px 8px',fontWeight:700,marginTop:5,display:'inline-block' }}>✓ {t('सत्यापित','Verified')}</span> : null}
                </div>
              </div>
            </div>
          ))
        )
      )}

      {/* Contribute hint */}
      {!loading && (
        <div style={{ marginTop:16,padding:'12px 14px',background:`${M}06`,borderRadius:12,border:`1px dashed ${M}35`,textAlign:'center' }}>
          <div style={{ fontSize:12,color:M,fontWeight:700,marginBottom:6 }}>
            {t('क्या आपके पास और जानकारी है?','Do you have more information?')}
          </div>
          <div style={{ fontSize:11,color:'#9ca3af',marginBottom:10 }}>
            {t('अपने गोत्र, कुलदेवी, कुलऋषि की जानकारी साझा करें। आपका योगदान पूरे समाज के काम आएगा।',
               'Share information about your Gotra, Kuldevi, Kulrishi. Your contribution will benefit the entire community.')}
          </div>
          <button onClick={() => { setCAboutGotra(''); setCData({}); setCType('general_info'); setCDone(false); setView('contribute'); }}
            style={{ background:M,color:'#fff',border:'none',borderRadius:20,padding:'7px 20px',fontSize:12,fontWeight:800,cursor:'pointer' }}>
            📖 {t('योगदान करें','Contribute')}
          </button>
        </div>
      )}
    </div>
  );
}

// Sub-components for contribution form fields
export function GotraContributeFields({ data, setData, t }) { // eslint-disable-line no-unused-vars
  const fields = [
    { key:'name',               label:'गोत्र नाम (हिंदी)',         en:'Gotra Name (Hindi)' },
    { key:'name_en',            label:'Gotra Name (English)',       en:'Gotra Name (English)' },
    { key:'pravara',            label:'प्रवर ऋषि (अल्पविराम से)',  en:'Pravara Rishis (comma-separated)' },
    { key:'veda',               label:'वेद',                       en:'Veda' },
    { key:'vedic_shakha',       label:'वैदिक शाखा',                en:'Vedic Shakha' },
    { key:'gotra_pravartaka',   label:'गोत्र प्रवर्तक',            en:'Founder Rishi' },
    { key:'kulrishi',           label:'कुलऋषि',                    en:'Kulrishi' },
    { key:'kuldevi',            label:'कुलदेवी',                   en:'Kuldevi' },
    { key:'geographical_origin',label:'भौगोलिक उत्पत्ति',          en:'Geographical Origin' },
    { key:'surnames',           label:'संबंधित उपनाम (अल्पविराम से)', en:'Associated Surnames' },
    { key:'description',        label:'विवरण',                     en:'Description' },
  ];
  return (
    <div style={{ marginBottom:12 }}>
      {fields.map(f => (
        <div key={f.key} style={{ marginBottom:9 }}>
          <div style={{ fontSize:11,fontWeight:700,color:'#374151',marginBottom:4 }}>{f.label}</div>
          <input value={data[f.key]||''} onChange={e => setData(d => ({ ...d, [f.key]:e.target.value }))}
            style={{ width:'100%',padding:'7px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:12,boxSizing:'border-box' }}
            placeholder={f.en} />
        </div>
      ))}
    </div>
  );
}

export function KuldeviContributeFields({ data, setData, t }) { // eslint-disable-line no-unused-vars
  const fields = [
    { key:'name',             label:'कुलदेवी नाम (हिंदी)',  en:'Kuldevi Name (Hindi)' },
    { key:'name_en',          label:'Kuldevi Name (English)',en:'Kuldevi Name (English)' },
    { key:'also_known_as',    label:'अन्य नाम',             en:'Other Names' },
    { key:'temple_location',  label:'मंदिर का पता',         en:'Temple Address' },
    { key:'temple_city',      label:'मंदिर शहर/गाँव',       en:'Temple City/Village' },
    { key:'temple_state',     label:'राज्य',                en:'State' },
    { key:'deity_type',       label:'देवी प्रकार',           en:'Deity Type (e.g. Mata, Devi, Shakti)' },
    { key:'festival_info',    label:'पूजा/उत्सव जानकारी',   en:'Festival/Worship Info' },
    { key:'mythological_story',label:'पौराणिक कथा',          en:'Mythological Story' },
  ];
  return (
    <div style={{ marginBottom:12 }}>
      {fields.map(f => (
        <div key={f.key} style={{ marginBottom:9 }}>
          <div style={{ fontSize:11,fontWeight:700,color:'#374151',marginBottom:4 }}>{f.label}</div>
          <input value={data[f.key]||''} onChange={e => setData(d => ({ ...d, [f.key]:e.target.value }))}
            style={{ width:'100%',padding:'7px 10px',border:'1px solid #e5e7eb',borderRadius:7,fontSize:12,boxSizing:'border-box' }}
            placeholder={f.en} />
        </div>
      ))}
    </div>
  );
}

export function SammelanTab() {
  const { t, lang } = useLang();
  const [upcoming, setUpcoming] = useState([]);
  const [past,     setPast]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showPast, setShowPast] = useState(false);
  // Detect user's local timezone
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const CK = 'sammelan_events';
    const cached = ssGet(CK, 5 * 60 * 1000);
    if (cached) { setUpcoming(cached.upcoming || []); setPast(cached.past || []); setLoading(false); return; }
    axios.get(`${baseUrl}/member/sammelan`, { headers: authHeader() })
      .then(r => {
        if (r.data.status) {
          setUpcoming(r.data.upcoming || []);
          setPast(r.data.past || []);
          ssSet(CK, { upcoming: r.data.upcoming, past: r.data.past });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isNRI = userTz && userTz !== 'Asia/Kolkata' && !userTz.startsWith('Asia/Kolkata');
  const virtualEvents = upcoming.filter(e => e.event_type === 'virtual' || e.event_type === 'hybrid');

  return (
    <div>
      {/* Header */}
      <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>🎪 {t('समाज समागम व कार्यक्रम', 'Samaj Events & Sammelan')}</div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 3 }}>
          {t('आगामी कार्यक्रम · Online & Offline', 'Upcoming events · Online & In-Person')}
        </div>
      </div>

      {/* NRI virtual events highlight */}
      {isNRI && virtualEvents.length > 0 && (
        <div style={{ background: '#eff6ff', border: '1.5px solid #bfdbfe', borderRadius: 12, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 22 }}>🌍</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#1e40af' }}>
              {t('आप विदेश में हैं — Online join करें!', 'You are abroad — join online!')}
            </div>
            <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
              {virtualEvents.length} {t('कार्यक्रम Online join कर सकते हैं ·', 'events you can join virtually ·')} {userTz}
            </div>
          </div>
        </div>
      )}

      {loading && <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>}

      {!loading && upcoming.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}>🗓️</div>
          <div style={{ fontSize: 13 }}>{t('अभी कोई आगामी कार्यक्रम नहीं है।', 'No upcoming events at the moment.')}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>{t('जल्द ही सूचित किया जाएगा।', 'You will be notified soon.')}</div>
        </div>
      )}

      {!loading && upcoming.map(ev => (
        <EventCard key={ev.id} ev={ev} lang={lang} userTz={userTz} />
      ))}

      {/* Past events toggle */}
      {!loading && past.length > 0 && (
        <>
          <button onClick={() => setShowPast(p => !p)} style={{ width: '100%', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '9px 0', fontSize: 12, fontWeight: 700, color: '#6b7280', cursor: 'pointer', marginTop: 4, marginBottom: 8 }}>
            {showPast ? '▲ ' : '▼ '}{t('पिछले कार्यक्रम', 'Past Events')} ({past.length})
          </button>
          {showPast && past.map(ev => <EventCard key={ev.id} ev={ev} lang={lang} userTz={userTz} />)}
        </>
      )}
    </div>
  );
}

// ── City Community Tab ────────────────────────────────────────────────────────
export function CityCommunityTab({ profile }) {
  const { t } = useLang();
  const [cities,      setCities]      = useState([]);
  const [cityLoading, setCityLoading] = useState(true);
  const [search,      setSearch]      = useState('');
  const [selectedCity,setSelectedCity]= useState(null);
  const [members,     setMembers]     = useState([]);
  const [memLoading,  setMemLoading]  = useState(false);

  const myCity    = profile?.temp_address?.city    || null;
  const myCountry = profile?.temp_address?.country || null;
  const myCf      = countryFlag(myCountry);

  // Load city stats on mount
  useEffect(() => {
    const CK = 'city_stats';
    const cached = ssGet(CK, 30 * 60 * 1000);
    if (cached) { setCities(cached); setCityLoading(false); return; }
    axios.get(`${baseUrl}/member/city-stats`, { headers: authHeader() })
      .then(r => { if (r.data.status) { setCities(r.data.result || []); ssSet(CK, r.data.result || []); } })
      .catch(() => {})
      .finally(() => setCityLoading(false));
  }, []);

  // Load members when a city is selected
  useEffect(() => {
    if (!selectedCity) { setMembers([]); return; }
    setMemLoading(true);
    axios.get(`${baseUrl}/member/city-members?city=${encodeURIComponent(selectedCity.city)}`, { headers: authHeader() })
      .then(r => { if (r.data.status) setMembers(r.data.result || []); })
      .catch(() => {})
      .finally(() => setMemLoading(false));
  }, [selectedCity]);

  const filtered = cities.filter(c =>
    !search || c.city?.toLowerCase().includes(search.toLowerCase()) ||
    c.state?.toLowerCase().includes(search.toLowerCase())
  );

  const myRow = myCity ? cities.find(c => c.city?.toLowerCase() === myCity.toLowerCase()) : null;

  // Auto-select user's own city once city stats load
  useEffect(() => {
    if (myRow && !selectedCity) setSelectedCity(myRow);
  }, [myRow]); // eslint-disable-line

  return (
    <div>
      {/* Header — maroon, consistent with portal */}
      <div style={{ background: `linear-gradient(135deg, ${M} 0%, #b71c1c 100%)`, borderRadius: 14, padding: '14px 16px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CompassOutlined style={{ fontSize: 22, color: '#fff' }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>{t('शहर समुदाय', 'City Community')}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
              {t('अपने शहर के राजपुरोहित सदस्य खोजें', 'Find Rajpurohit members in your city')}
            </div>
          </div>
        </div>
      </div>

      {/* My City hero card */}
      {myCity && myRow && !selectedCity && (
        <div onClick={() => setSelectedCity(myRow)} style={{
          background: `linear-gradient(135deg, ${M}, #b71c1c)`,
          borderRadius: 14, padding: '14px 16px', marginBottom: 14,
          cursor: 'pointer', border: `2px solid ${G}40`,
          display: 'flex', alignItems: 'center', gap: 14,
          boxShadow: `0 4px 16px ${M}30`,
        }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {myCf?.isAbroad
              ? <span style={{ fontSize: 28 }}>{myCf.flag}</span>
              : <CompassOutlined style={{ fontSize: 22, color: '#fff' }} />
            }
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {t('आपका शहर', 'Your City')}
            </div>
            <div style={{ fontSize: 16, fontWeight: 900, color: '#fff', marginTop: 2 }}>{toTitle(myCity)}</div>
            {myRow.state && (
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>
                {toTitle(myRow.state)}{myRow.country && myRow.country.toLowerCase() !== 'india' ? ` · ${toTitle(myRow.country)}` : ''}
              </div>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: G }}>{myRow.member_count}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)' }}>{t('सदस्य', 'members')}</div>
            <div style={{ fontSize: 11, color: G, marginTop: 4, fontWeight: 700 }}>{t('देखें →', 'View →')}</div>
          </div>
        </div>
      )}

      {/* Selected city — member list */}
      {selectedCity && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button onClick={() => setSelectedCity(null)} style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
              ← {t('वापस', 'Back')}
            </button>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: M, display: 'flex', alignItems: 'center', gap: 6 }}>
                <CompassOutlined style={{ fontSize: 14 }} /> {toTitle(selectedCity.city)}
              </div>
              <div style={{ fontSize: 10, color: '#6b7280' }}>
                {selectedCity.member_count} {t('सदस्य', 'members')}
                {selectedCity.state ? ` · ${toTitle(selectedCity.state)}` : ''}
              </div>
            </div>
          </div>

          {memLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
          {!memLoading && members.map(m => {
            const cf = countryFlag(m.current_country);
            return (
              <div key={m.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '10px 14px', marginBottom: 8, display: 'flex', gap: 12, alignItems: 'center', boxShadow: '0 1px 5px rgba(0,0,0,0.05)' }}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  {m.photo
                    ? <img src={m.photo} alt={m.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${M}` }} />
                    : <div style={{ width: 44, height: 44, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 800, color: G }}>
                        {(m.name || '?')[0].toUpperCase()}
                      </div>
                  }
                  {cf?.isAbroad && <span style={{ position: 'absolute', bottom: -3, right: -3, fontSize: 13 }}>{cf.flag}</span>}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{m.name}</div>
                  {m.father_name && <div style={{ fontSize: 10, color: '#6b7280' }}>{t('पिता', 'Father')}: {m.father_name}</div>}
                  <div style={{ fontSize: 10, color: '#9ca3af' }}>
                    {[m.village, m.district].filter(Boolean).join(', ')}
                    {m.gotra ? ` · ${t('गोत्र', 'Gotra')}: ${m.gotra}` : ''}
                  </div>
                </div>
                {m.membership_no && <div style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }}>#{m.membership_no}</div>}
              </div>
            );
          })}
          {!memLoading && members.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>{t('कोई सदस्य नहीं मिला', 'No members found')}</div>
          )}
        </div>
      )}

      {/* City search + list */}
      {!selectedCity && (
        <>
          <Input
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder={t('शहर खोजें… जैसे: Bengaluru, Mumbai, Dubai', 'Search city… e.g. Bengaluru, Mumbai, Dubai')}
            allowClear
            style={{ borderRadius: 10, marginBottom: 12, height: 42 }}
          />

          {cityLoading && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
          {!cityLoading && filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
              <CompassOutlined style={{ fontSize: 32, display: 'block', margin: '0 auto 8px', color: '#d1d5db' }} />
              {t('कोई शहर नहीं मिला', 'No city found')}
            </div>
          )}

          {!cityLoading && filtered.map((c, i) => {
            const cf       = countryFlag(c.country);
            const isAbroad = cf?.isAbroad;
            const isMe     = myCity && c.city?.toLowerCase() === myCity.toLowerCase();
            const pct      = Math.min(100, (c.member_count / (filtered[0]?.member_count || 1)) * 100);
            return (
              <div key={c.city} onClick={() => setSelectedCity(c)} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: isMe ? '#fff5f5' : '#fff',
                border: isMe ? `1.5px solid ${M}` : '1px solid #e5e7eb',
                borderRadius: 12, padding: '10px 14px', marginBottom: 8,
                cursor: 'pointer', boxShadow: '0 1px 5px rgba(0,0,0,0.05)',
              }}>
                {/* Rank badge */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                  background: i < 3 ? M : '#f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 900,
                  color: i < 3 ? '#fff' : '#6b7280',
                }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {toTitle(c.city)}
                    </div>
                    {isMe && <span style={{ fontSize: 9, background: M, color: '#fff', borderRadius: 20, padding: '1px 7px', fontWeight: 700, flexShrink: 0 }}>YOU</span>}
                    {isAbroad && cf?.flag && <span style={{ fontSize: 14, flexShrink: 0 }}>{cf.flag}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                    {[c.state, c.country].filter(Boolean).map(toTitle).join(', ')}
                  </div>
                  <div style={{ height: 4, background: '#f3f4f6', borderRadius: 3, marginTop: 6 }}>
                    <div style={{ height: '100%', borderRadius: 3, background: `linear-gradient(90deg,${M},#b71c1c)`, width: `${pct}%`, transition: 'width 0.5s' }} />
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 900, color: M }}>{c.member_count}</div>
                  <div style={{ fontSize: 9, color: '#9ca3af' }}>{t('सदस्य', 'members')}</div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Village Directory Tab ─────────────────────────────────────────────────────
export function VillageDirectoryTab({ defaultVillage = '' }) {
  const { t } = useLang();
  const [villages,   setVillages]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState(defaultVillage);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [viewVillage,  setViewVillage]  = useState(null);
  const [pramukh,      setPramukh]     = useState(null);
  const [pramukhLoad,  setPramukhLoad] = useState(false);
  const [villageStats, setVillageStats]= useState(null);
  const [statsLoad,    setStatsLoad]   = useState(false);
  const [vMembers,    setVMembers]    = useState([]); // eslint-disable-line no-unused-vars
  const [vMemLoading, setVMemLoading] = useState(false); // eslint-disable-line no-unused-vars
  const dSearch = useDebounce(search, 400);
  const PAGE_SIZE = 20;

  const fetchVillages = useCallback(async (pg = 1, s = dSearch) => {
    setLoading(true);
    try {
      const r = await axios.get(`${baseUrl}/member/village-list?search=${encodeURIComponent(s)}&page=${pg}&limit=${PAGE_SIZE}`, { headers: authHeader() });
      if (r.data.status) { setVillages(r.data.result || []); setTotal(r.data.total || 0); }
    } catch {} finally { setLoading(false); }
  }, [dSearch]); // eslint-disable-line

  useEffect(() => { setPage(1); fetchVillages(1, dSearch); }, [dSearch]); // eslint-disable-line

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const openDetail = (v) => {
    setViewVillage(v);
    setVillageStats(null);

    // Gram Pramukh — use inline data if available, else fetch
    if (v.pramukh_user_id) {
      setPramukh({ user_id: v.pramukh_user_id, name: v.pramukh_name, photo: v.pramukh_photo, phone: v.pramukh_phone });
    } else {
      setPramukh(null);
      setPramukhLoad(true);
      axios.get(`${baseUrl}/member/gram-pramukh?village_id=${v.id}`, { headers: authHeader() })
        .then(r => setPramukh(r.data.result || null))
        .catch(() => setPramukh(null))
        .finally(() => setPramukhLoad(false));
    }

    // Village stats
    setStatsLoad(true);
    const params = new URLSearchParams({ village_id: v.id });
    if (v.name_hn) params.set('village_name_hn', v.name_hn);
    if (v.name_en) params.set('village_name_en', v.name_en);
    axios.get(`${baseUrl}/member/village-stats?${params}`, { headers: authHeader() })
      .then(r => setVillageStats(r.data.result || null))
      .catch(() => setVillageStats(null))
      .finally(() => setStatsLoad(false));
  };

  return (
    <div>
      <div style={{ fontSize: 15, fontWeight: 800, color: M, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <EnvironmentOutlined /> Village Directory
      </div>

      <Input prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
        placeholder="Search village in Hindi or English…"
        value={search} onChange={e => setSearch(e.target.value)} allowClear
        style={{ borderRadius: 8, marginBottom: 14 }} />

      {loading ? <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
        : !villages.length ? <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>No village found</div>
        : (
          <>
            <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 10 }}>{total} villages · page {page} of {totalPages}</div>
            {villages.map(v => (
              <div key={v.id} style={{ background: '#fff', borderRadius: 10, border: v.pramukh_user_id ? `1.5px solid #f59e0b` : '1px solid #e5e7eb', padding: '10px 14px', marginBottom: 6 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <EnvironmentOutlined style={{ color: M, fontSize: 14, marginTop: 2, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {v.name_hn && <span style={{ fontSize: 13, fontWeight: 800, color: '#111827' }}>{v.name_hn}</span>}
                      {v.name_en && <span style={{ fontSize: 11, color: '#6b7280' }}>{v.name_en}</span>}
                      {v.pramukh_user_id && (
                        <span style={{ fontSize: 9, background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24', borderRadius: 20, padding: '1px 7px', fontWeight: 700 }}>
                          👑 {t('ग्राम प्रमुख', 'Gram Pramukh')}
                        </span>
                      )}
                    </div>
                    {(v.tehsil_hn || v.tehsil_en) && (
                      <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                        {t('तहसील','Tehsil')}: {v.tehsil_hn || ''}{v.tehsil_hn && v.tehsil_en ? ' / ' : ''}{v.tehsil_en || ''}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1 }}>
                      {[v.district_hn, v.district_en && `(${v.district_en})`, v.state_hn || v.state_en].filter(Boolean).join(' ')}
                      {v.pincode_en ? ` · ${v.pincode_en}` : ''}
                    </div>
                    {/* Gram Pramukh mini preview on list */}
                    {v.pramukh_name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
                        {v.pramukh_photo
                          ? <img src={v.pramukh_photo} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #fbbf24' }} />
                          : <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, color: '#fef3c7' }}>{v.pramukh_name[0]}</div>
                        }
                        <span style={{ fontSize: 10, color: '#92400e', fontWeight: 700 }}>{v.pramukh_name}</span>
                      </div>
                    )}
                  </div>
                  <button onClick={() => openDetail(v)} style={{
                    background: v.pramukh_user_id ? '#fef3c7' : `${M}15`,
                    border: `1px solid ${v.pramukh_user_id ? '#fbbf24' : `${M}40`}`,
                    borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 700,
                    color: v.pramukh_user_id ? '#92400e' : M, cursor: 'pointer', flexShrink: 0,
                  }}>{t('देखें', 'View')}</button>
                </div>
              </div>
            ))}

            {/* ── Village Detail Modal ── */}
            {viewVillage && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                onClick={() => setViewVillage(null)}>
                <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', padding: '20px 18px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
                  onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 18, fontWeight: 900, color: M }}>{viewVillage.name_hn || viewVillage.name_en}</div>
                      {viewVillage.name_en && viewVillage.name_hn && <div style={{ fontSize: 12, color: '#6b7280' }}>{viewVillage.name_en}</div>}
                    </div>
                    <button onClick={() => setViewVillage(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                  </div>

                  {/* Details grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'तहसील / Tehsil', value: [viewVillage.tehsil_hn, viewVillage.tehsil_en].filter(Boolean).join(' / ') },
                      { label: 'जिला / District', value: [viewVillage.district_hn, viewVillage.district_en].filter(Boolean).join(' / ') },
                      { label: 'राज्य / State',   value: [viewVillage.state_hn, viewVillage.state_en].filter(Boolean).join(' / ') },
                      { label: 'पिनकोड / Pincode', value: viewVillage.pincode_en || '—' },
                      { label: 'पोस्ट / Post',    value: [viewVillage.post_hn, viewVillage.post_en].filter(Boolean).join(' / ') || '—' },
                    ].map(d => (
                      <div key={d.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 10px' }}>
                        <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', marginBottom: 2 }}>{d.label}</div>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{d.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* ── Village Progress Stats ── */}
                  {statsLoad && <div style={{ textAlign: 'center', padding: 14 }}><Spin size="small" /></div>}
                  {!statsLoad && villageStats && (() => {
                    const s = villageStats;
                    const paidPct = s.total_members > 0 ? Math.min(100, Math.round((s.paid_this_month / s.total_members) * 100)) : 0;
                    const fyPct   = s.total_members > 0 ? Math.min(100, Math.round((s.paid_this_fy   / s.total_members) * 100)) : 0;
                    return (
                      <div style={{ marginBottom: 14 }}>
                        {/* Header */}
                        <div style={{ background: `linear-gradient(135deg, ${M}, #b71c1c)`, borderRadius: '12px 12px 0 0', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ fontSize: 12, fontWeight: 900, color: '#fff' }}>📊 {t('ग्राम प्रगति रिपोर्ट', 'Village Progress')}</div>
                          {s.leaderboard_rank && (
                            <span style={{ background: G, color: M, fontSize: 10, fontWeight: 900, borderRadius: 20, padding: '2px 10px' }}>
                              🏆 #{s.leaderboard_rank} {t('रैंक', 'Rank')}
                            </span>
                          )}
                        </div>

                        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '12px 14px' }}>
                          {/* Stat grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                            {[
                              { label: t('कुल सदस्य', 'Total Members'),       value: s.total_members,   color: M,         icon: '👥' },
                              { label: t('इस माह सक्रिय', 'Active This Month'), value: s.paid_this_month, color: '#059669',  icon: '✅' },
                              { label: t('इस वर्ष योगदान', `FY ${s.fy_label}`), value: `₹${Number(s.collected_fy).toLocaleString('en-IN')}`, color: '#d97706', icon: '💛' },
                              { label: t('नए सदस्य (माह)', 'New This Month'),  value: s.new_this_month,  color: '#7c3aed',  icon: '🆕' },
                            ].map(st => (
                              <div key={st.label} style={{ background: '#f9fafb', borderRadius: 10, padding: '9px 11px', border: `1px solid ${st.color}20` }}>
                                <div style={{ fontSize: 16, marginBottom: 2 }}>{st.icon}</div>
                                <div style={{ fontSize: 18, fontWeight: 900, color: st.color }}>{st.value}</div>
                                <div style={{ fontSize: 9, color: '#6b7280', fontWeight: 600, marginTop: 2 }}>{st.label}</div>
                              </div>
                            ))}
                          </div>

                          {/* Payment participation bars */}
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 4 }}>
                              <span>💳 {t('इस माह भुगतान', 'Paid This Month')} ({s.paid_this_month}/{s.total_members})</span>
                              <span style={{ fontWeight: 700, color: '#059669' }}>{paidPct}%</span>
                            </div>
                            <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#059669,#34d399)', width: `${paidPct}%`, transition: 'width 0.8s ease' }} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#6b7280', marginBottom: 4 }}>
                              <span>📅 {t('इस वर्ष योगदान', `FY ${s.fy_label} Contributed`)} ({s.paid_this_fy}/{s.total_members})</span>
                              <span style={{ fontWeight: 700, color: M }}>{fyPct}%</span>
                            </div>
                            <div style={{ height: 8, background: '#f3f4f6', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', borderRadius: 4, background: `linear-gradient(90deg,${M},#ef4444)`, width: `${fyPct}%`, transition: 'width 0.8s ease' }} />
                            </div>
                          </div>

                          {/* Census families */}
                          {s.census_families > 0 && (
                            <div style={{ background: '#eff6ff', borderRadius: 8, padding: '8px 11px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 18 }}>📋</span>
                              <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af' }}>{s.census_families} {t('जनगणना परिवार', 'Census Families')}</div>
                                <div style={{ fontSize: 10, color: '#3b82f6' }}>{t('जनगणना में दर्ज', 'Recorded in census')}</div>
                              </div>
                            </div>
                          )}

                          {/* Recent members */}
                          {s.recent_members?.length > 0 && (
                            <>
                              <div style={{ fontSize: 10, fontWeight: 700, color: '#374151', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                🆕 {t('हाल ही में जुड़े सदस्य', 'Recently Joined')}
                              </div>
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {s.recent_members.map(m => (
                                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 20, padding: '3px 9px 3px 4px' }}>
                                    {m.photo
                                      ? <img src={m.photo} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `1px solid ${M}` }} />
                                      : <div style={{ width: 20, height: 20, borderRadius: '50%', background: M, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 900, color: G }}>{(m.name || '?')[0]}</div>
                                    }
                                    <span style={{ fontSize: 10, fontWeight: 600, color: '#374151' }}>{m.name}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* ── Gram Pramukh contact card ── */}
                  {pramukhLoad && (
                    <div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
                  )}
                  {!pramukhLoad && pramukh && (
                    <div style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fbbf24', borderRadius: 12, padding: '12px 14px', marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
                        👑 {t('ग्राम प्रमुख', 'Gram Pramukh')} — {t('आपके गाँव का प्रतिनिधि', 'Your Village Champion')}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {pramukh.photo
                          ? <img src={pramukh.photo} alt={pramukh.name} style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: '2.5px solid #fbbf24', flexShrink: 0 }} />
                          : <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 900, color: '#fef3c7', flexShrink: 0 }}>
                              {(pramukh.name || '?')[0]}
                            </div>
                        }
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 900, color: '#78350f' }}>{pramukh.name}</div>
                          {pramukh.village && <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>{[pramukh.village, pramukh.district].filter(Boolean).join(', ')}</div>}
                          {pramukh.notes && <div style={{ fontSize: 11, color: '#b45309', marginTop: 3, fontStyle: 'italic' }}>"{pramukh.notes}"</div>}
                        </div>
                      </div>
                      {pramukh.phone && (
                        <a href={`tel:${pramukh.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 10, background: '#92400e', color: '#fef3c7', borderRadius: 10, padding: '9px 0', fontSize: 13, fontWeight: 800, textDecoration: 'none' }}>
                          📞 {t('संपर्क करें', 'Contact')} — {pramukh.phone}
                        </a>
                      )}
                    </div>
                  )}
                  {!pramukhLoad && !pramukh && (
                    <div style={{ background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: 10, padding: '10px 14px', marginBottom: 14, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>
                        👑 {t('इस गाँव का ग्राम प्रमुख अभी नियुक्त नहीं है।', 'No Gram Pramukh assigned for this village yet.')}
                      </div>
                    </div>
                  )}

                  {/* History / Remark — always shown */}
                  <div style={{ background: viewVillage.remark ? '#fef9c3' : '#f9fafb', border: `1px solid ${viewVillage.remark ? '#fde047' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: viewVillage.remark ? '#713f12' : '#9ca3af', marginBottom: 6 }}>
                      📜 {t('ग्राम इतिहास', 'Village History')}
                    </div>
                    <div style={{ fontSize: 13, color: viewVillage.remark ? '#374151' : '#9ca3af', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {viewVillage.remark || t('इस गाँव का इतिहास उपलब्ध नहीं है।', 'No village history available.')}
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
                <button onClick={() => { const p = Math.max(1, page-1); setPage(p); fetchVillages(p); }}
                  disabled={page === 1}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: page===1?'#f9fafb':'#fff', cursor: page===1?'default':'pointer', fontSize: 12 }}>
                  ← Prev
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pg = page <= 3 ? i+1 : page >= totalPages-2 ? totalPages-4+i : page-2+i;
                  if (pg < 1 || pg > totalPages) return null;
                  return (
                    <button key={pg} onClick={() => { setPage(pg); fetchVillages(pg); }}
                      style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12, cursor: 'pointer',
                        background: pg === page ? M : '#fff', color: pg === page ? G : '#374151', fontWeight: pg === page ? 700 : 400 }}>
                      {pg}
                    </button>
                  );
                })}
                <button onClick={() => { const p = Math.min(totalPages, page+1); setPage(p); fetchVillages(p); }}
                  disabled={page === totalPages}
                  style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: page===totalPages?'#f9fafb':'#fff', cursor: page===totalPages?'default':'pointer', fontSize: 12 }}>
                  Next →
                </button>
              </div>
            )}
          </>
        )
      }
    </div>
  );
}
